import { getAdminParents } from './getAdminParents';
import type { AdminParent } from './getAdminParents';
import type { ParentFirmAdminContextModel } from './index';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { Access, SecurityPackage } from '@thinkalpha/access-ws-client/contracts/user.js';
import type { FirmWithParentsAndChildren } from '@thinkalpha/ui-types/types/admin/firms';
import { inject, injectable } from 'src/features/ioc';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { getUserAccessListQuery } from 'src/queries/user';
import type { ReactBindings } from 'src/types/bindings';
import type { z } from 'zod';

@injectable()
export class ParentFirmAdminContextModelImpl implements ParentFirmAdminContextModel {
    readonly #disposableStack = new DisposableStack();
    readonly #firm = signal<FirmWithParentsAndChildren | null>(null);
    readonly #accessListQuery: QueryObserverModel<z.infer<typeof SecurityPackage>, Error, Access[]>;

    readonly adminParents: ReadonlySignal<readonly AdminParent[]> = computed(() => {
        const firm = this.#firm.value;
        if (!firm) return [];
        return getAdminParents(firm, this.#accessListQuery.signal.value.data);
    });

    constructor(
        @inject('QueryObserverModelFactory')
        queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('CurrentUserModel')
        currentUserModel: ReactBindings['CurrentUserModel'],
    ) {
        this.#accessListQuery = queryObserverModelFactory<z.infer<typeof SecurityPackage>, Error, Access[]>(
            computed(() => getUserAccessListQuery(currentUserModel.userId.value)),
        );
        this.#disposableStack.use(this.#accessListQuery);
    }

    setFirm(firm: FirmWithParentsAndChildren | null): void {
        if (this.#firm.value === firm) return;
        this.#firm.value = firm;
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
